/**
 * Account lifecycle (`profiles.status`) — the frontend mirror.
 *
 * Kept in lockstep with backend `config/constants.py` (ACCOUNT_STATUSES / ACCESS_BLOCKED_STATUSES)
 * and with `public.account_access_blocked()` in the database, exactly the way `lib/roles.ts` and
 * `lib/capabilities.ts` mirror their backend halves. All three must agree; the backend and the
 * database are the enforcement, this file is only the UI's copy of the rule.
 *
 * Status is ORTHOGONAL to role and to capabilities. Role says what the account could do; status
 * says whether it may act at all. A buyer who holds every buyer capability is still parked at
 * /pending-approval while an admin has not decided — so this is checked ALONGSIDE
 * `useCapabilities`, never folded into it.
 *
 *   draft            → onboarding unfinished. OnboardingGuard owns this one; NOT blocked here,
 *                      because blocking it would deny the endpoints that get a user out of draft.
 *   pending_approval → buyer awaiting a platform admin.
 *   active           → may act.
 *   rejected         → an admin refused the application.
 *   suspended        → an admin revoked an account that was active.
 *   null             → legacy rows that predate the column. Deliberately NOT blocked: a deny-list
 *                      of the three new statuses, not an allow-list of 'active', is what keeps
 *                      every pre-existing account working.
 */
import { apiClient } from "./api-client";

export const ACCOUNT_STATUS = {
  DRAFT: "draft",
  PENDING_APPROVAL: "pending_approval",
  ACTIVE: "active",
  REJECTED: "rejected",
  SUSPENDED: "suspended",
} as const;

export type AccountStatus = (typeof ACCOUNT_STATUS)[keyof typeof ACCOUNT_STATUS];

/** The statuses that deny all product access. Mirrors backend ACCESS_BLOCKED_STATUSES. */
export const ACCESS_BLOCKED_STATUSES: ReadonlySet<string> = new Set([
  ACCOUNT_STATUS.PENDING_APPROVAL,
  ACCOUNT_STATUS.REJECTED,
  ACCOUNT_STATUS.SUSPENDED,
]);

export function isAccessBlockedStatus(status: string | null | undefined): boolean {
  return !!status && ACCESS_BLOCKED_STATUSES.has(status);
}

/** Copy for the page a blocked account is parked on. Headline, body, and whether to offer support. */
export const BLOCKED_STATUS_COPY: Record<
  string,
  { title: string; body: string; tone: "waiting" | "refused" }
> = {
  [ACCOUNT_STATUS.PENDING_APPROVAL]: {
    title: "Your account is under review",
    body:
      "Thanks for signing up. A member of the Zen Music team reviews every new licensee account " +
      "before it is opened. You will get an email as soon as yours is approved — there is nothing " +
      "else you need to do.",
    tone: "waiting",
  },
  [ACCOUNT_STATUS.REJECTED]: {
    title: "Your account was not approved",
    body:
      "The Zen Music team has reviewed your application and was not able to approve it. " +
      "If you think this is a mistake, get in touch and we will take another look.",
    tone: "refused",
  },
  [ACCOUNT_STATUS.SUSPENDED]: {
    title: "Your account is suspended",
    body:
      "Access to this account has been suspended. Contact the Zen Music team to find out why and " +
      "to have it restored.",
    tone: "refused",
  },
};

// ── API ──────────────────────────────────────────────────────────────────────

export interface AccountStatusResponse {
  user_id: string;
  role: string | null;
  status: string | null;
  organization_id: string | null;
  blocked: boolean;
  /** The server's own sentence for this status, when it has one. */
  message: string | null;
}

/**
 * Read the caller's own lifecycle status from the server.
 *
 * This is the ONE endpoint a blocked account can still call — every other route refuses it —
 * which is what makes it usable as the "am I approved yet?" poll on the waiting page.
 */
export async function fetchOwnAccountStatus(): Promise<AccountStatusResponse> {
  return apiClient.get<AccountStatusResponse>("/api/account/status");
}

// ── admin queue ──────────────────────────────────────────────────────────────

export interface AccountReviewOrganization {
  id: string | null;
  name: string | null;
  type: string | null;
  country: string | null;
  website: string | null;
  contact_email: string | null;
}

export interface AccountForReview {
  user_id: string;
  email: string | null;
  display_name: string | null;
  role: string | null;
  org_role: string | null;
  status: string | null;
  position: string | null;
  phone: string | null;
  label_tag: string | null;
  signed_up_at: string | null;
  approval_requested_at: string | null;
  approval_decided_at: string | null;
  approval_decided_by: string | null;
  approval_note: string | null;
  organization: AccountReviewOrganization | null;
  /**
   * Buyers only: the buying organization's tax evidence and whether it may pay. Null for
   * sellers, and when the evidence could not be loaded — never read as "no evidence".
   */
  tax: AccountReviewTax | null;
  /** What an approving admin should look twice at. Flags, never blocks. */
  risk_flags: AccountReviewRiskFlag[];
}

export interface AccountReviewTax {
  business_attested_at: string | null;
  legal_name: string | null;
  tax_country: string | null;
  tax_id_type: string | null;
  tax_id_value: string | null;
  no_tax_id_reason: string | null;
  tax_id_verification: "pending" | "verified" | "unverified" | "unavailable" | null;
  tax_id_verified_name: string | null;
  ready: boolean;
  classification: "consumer" | "business_verified" | "business_unverified";
}

export interface AccountReviewRiskFlag {
  code:
    | "personal_email"
    | "no_website"
    | "consumer_rules_country"
    | "not_attested"
    | "tax_id_rejected";
  label: string;
}

export type AccountReviewAction = "approve" | "reject" | "suspend";

export interface AccountApprovalQueue {
  items: AccountForReview[];
  total: number;
  has_more: boolean;
  pending_count: number;
}

export async function adminListAccountApprovals(params: {
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<AccountApprovalQueue> {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  query.set("limit", String(params.limit ?? 50));
  query.set("offset", String(params.offset ?? 0));
  return apiClient.get<AccountApprovalQueue>(
    `/api/admin/account-approvals?${query.toString()}`
  );
}

export async function adminPendingAccountCount(): Promise<number> {
  const res = await apiClient.get<{ pending_count: number }>(
    "/api/admin/account-approvals/pending-count"
  );
  return res.pending_count ?? 0;
}

export interface AccountReviewResult {
  user_id: string;
  previous_status: string | null;
  status: string;
  role: string | null;
  email: string | null;
  display_name: string | null;
  approval_note: string | null;
  decided_by: string;
  /** False when the decision stuck but the notification email did not go out. */
  email_sent: boolean;
}

export async function adminReviewAccount(
  userId: string,
  action: AccountReviewAction,
  note?: string | null
): Promise<AccountReviewResult> {
  return apiClient.post<AccountReviewResult>(
    `/api/admin/account-approvals/${userId}/review`,
    { action, note: note?.trim() || null }
  );
}
